import React, { useEffect, useRef, useState } from 'react';

/**
 * Google Identity Services sign-in button for the Funeral Professional
 * portal. Self-mounts the GIS SDK once (idempotent) and renders the
 * official pill-shaped button. When the user completes the flow, the
 * `credential` (ID token) is posted to `/api/fd/google-signin`; the
 * backend verifies, then returns our standard `{ token, account }`
 * shape so the dashboard can log the user in without any password.
 *
 * Props:
 *   onSuccess({ token, account, created })   invoked on successful sign-in
 *   onError(message)                          invoked on any failure
 *   label                                     'signup_with' | 'signin_with' | 'continue_with'
 *
 * If REACT_APP_GOOGLE_CLIENT_ID is not set at build time, the component
 * renders nothing — this keeps the surface silent until you've finished
 * setting up OAuth in Google Cloud Console.
 */
const GSI_SRC = 'https://accounts.google.com/gsi/client';
const CLIENT_ID = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();

let _gsiLoadPromise = null;
function loadGsi() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.google && window.google.accounts && window.google.accounts.id) {
    return Promise.resolve(window.google);
  }
  if (_gsiLoadPromise) return _gsiLoadPromise;
  _gsiLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Google SDK')), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => reject(new Error('Failed to load Google SDK'));
    document.head.appendChild(s);
  });
  return _gsiLoadPromise;
}

const FdGoogleButton = ({ onSuccess, onError, label = 'continue_with' }) => {
  const mountRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!CLIENT_ID) return; // nothing to render without a client id
    let cancelled = false;

    loadGsi()
      .then((google) => {
        if (cancelled || !google || !mountRef.current) return;
        google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: async (response) => {
            if (!response || !response.credential) {
              onError && onError('Google did not return a credential.');
              return;
            }
            try {
              setSubmitting(true);
              const res = await fetch('/api/fd/google-signin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential: response.credential }),
              });
              const data = await res.json().catch(() => ({}));
              if (!res.ok) {
                onError && onError(data.error || 'Google sign-in failed');
                return;
              }
              onSuccess && onSuccess(data);
            } catch (e) {
              onError && onError(e.message || 'Google sign-in failed');
            } finally {
              setSubmitting(false);
            }
          },
          ux_mode: 'popup',
          auto_select: false,
        });
        google.accounts.id.renderButton(mountRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: label,
          shape: 'pill',
          logo_alignment: 'left',
          width: Math.min(380, (mountRef.current.parentElement?.offsetWidth || 360)),
        });
        setReady(true);
      })
      .catch((e) => {
        onError && onError(e.message || 'Failed to load Google sign-in');
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label]);

  if (!CLIENT_ID) return null;

  return (
    <div className="fd-google-wrap" aria-busy={submitting}>
      <div ref={mountRef} className="fd-google-mount" />
      {!ready && <div className="fd-google-placeholder" aria-hidden="true" />}
    </div>
  );
};

export default FdGoogleButton;
